// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import cancelDraftFieldValues from "@store/Framework/Store/Greif/Components/SimpleQuoteForm/Handlers/CancelDraftFieldValues";
import saveDraftFieldValues from "@store/Framework/Store/Greif/Components/SimpleQuoteForm/Handlers/SaveDraftFieldValues";
import setDraftFieldValues from "@store/Framework/Store/Greif/Components/SimpleQuoteForm/Handlers/SetDraftFieldValues";
import { useStepperContextStep } from "@store/Framework/Components/StepperContext";
import { SimpleQuoteFormFieldValues } from "@store/Framework/Store/Greif/Components/SimpleQuoteForm/SimpleQuoteFormState";
import FormStep, { FormStepPresentationProps, FormStepProps } from "@store/Library/Components/FormStepper/FormStep";
import TextAreaFormField, {
    TextAreaFormFieldPresentationProps,
} from "@store/Library/Components/FormField/TextAreaFormField";
import { formStepProps } from "@store/Themes/common/props/formStep";
import React, { FC, useState } from "react";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { css } from "styled-components";
import siteMessage from "@insite/client-framework/SiteMessage";
import FileUploadFormField, {
    FileUploadFormFieldPresentationProps,
} from "@store/Library/Components/FormField/FileUploadFormField";
import constants from "@store/constants";
import FormFieldUploadedFile from "@store/Library/Components/FormField/FormFieldUploadedFile";
import TextFormField, { TextFormFieldPresentationProps } from "@store/Library/Components/FormField/TextFormField";

const stepFieldValueKeys: Array<keyof SimpleQuoteFormFieldValues> = [
    "estimatedAnnualQuantity",
    "attachments",
    "requestDetails",
];

export interface SimpleQuoteFormDetailsStepProps extends Partial<Omit<FormStepProps, "contextStepNumber">> {}

export interface SimpleQuoteFormDetailsStepStyles {
    attachmentsInfoValue?: TypographyPresentationProps;
    estimatedAnnualQuantityFormField?: TextFormFieldPresentationProps;
    fileUploadFormField?: FileUploadFormFieldPresentationProps;
    formGridContainer?: GridContainerProps;
    formGridItem?: GridItemProps;
    formStep?: FormStepPresentationProps;
    infoGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    infoLabel?: TypographyPresentationProps;
    infoValue?: TypographyPresentationProps;
    requestDetailsFormField?: TextAreaFormFieldPresentationProps;
}

export const simpleQuoteFormDetailsStepStyles: SimpleQuoteFormDetailsStepStyles = {
    formGridContainer: formStepProps.formGridContainer,
    formGridItem: {
        ...formStepProps.formGridItem,
        width: [12, 12, 6, 6, 6],
    },
    infoGridContainer: formStepProps.infoGridContainer,
    infoGridItem: formStepProps.infoGridItem,
    infoLabel: formStepProps.infoLabel,
    infoValue: formStepProps.infoValue,
    attachmentsInfoValue: mergeProps<TypographyPresentationProps>(formStepProps.infoValue, {
        forwardAs: "div",
        css: css`
            width: 100%;
        `,
    }),
};

const styles = simpleQuoteFormDetailsStepStyles;

const SimpleQuoteFormDetailsStep: FC<SimpleQuoteFormDetailsStepProps> = ({ ...formStepProps }) => {
    const dispatch = useAppDispatch();
    const [showErrors, setShowErrors] = useState(false);
    const { isComplete, stepNumber } = useStepperContextStep();
    const draftFieldValues = useAppSelector(state => state.greif.components.simpleQuoteForm.formDraftFieldValues);
    const fieldValues = useAppSelector(state => state.greif.components.simpleQuoteForm.formFieldValues);

    if (!stepNumber) {
        return null;
    }

    const attachmentsLabel = translate("Spec Sheet, Product SDS, or Other Documentation");
    const estimatedAnnualQuantityLabel = translate("Estimated Annual Quantity");
    const requestDetailsLabel = translate("Request Details");
    const requestDetailsErrorMessage = siteMessage("SimpleQuoteForm_RequestDetailsRequired").toString();
    const savedAttachments = fieldValues.attachments.value;

    return (
        <FormStep
            {...styles.formStep}
            contextStepNumber={stepNumber}
            onCancel={() => dispatch(cancelDraftFieldValues(stepFieldValueKeys))}
            onSubmit={event => {
                if (!showErrors) {
                    setShowErrors(true);
                }

                if (!stepFieldValueKeys.every(x => draftFieldValues[x].isValid !== false)) {
                    event.preventDefault();
                    return;
                }

                dispatch(saveDraftFieldValues(stepFieldValueKeys));
            }}
            title={translate("Details")}
            {...formStepProps}
        >
            {isComplete ? (
                <GridContainer {...styles.infoGridContainer}>
                    <GridItem {...styles.infoGridItem}>
                        <Typography {...styles.infoLabel}>{estimatedAnnualQuantityLabel}</Typography>
                        <Typography {...styles.infoValue}>
                            {fieldValues.estimatedAnnualQuantity.value || "—"}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.infoGridItem}>
                        <Typography {...styles.infoLabel}>{attachmentsLabel}</Typography>
                        <Typography {...styles.attachmentsInfoValue}>
                            {savedAttachments.length
                                ? savedAttachments.map((attachment, index) => (
                                      <FormFieldUploadedFile
                                          file={attachment}
                                          key={`${attachment.fileName}-${index}`}
                                      />
                                  ))
                                : "—"}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.infoGridItem} width={12}>
                        <Typography {...styles.infoLabel}>{requestDetailsLabel}</Typography>
                        <Typography {...styles.infoValue}>{fieldValues.requestDetails.value || "—"}</Typography>
                    </GridItem>
                </GridContainer>
            ) : (
                <GridContainer {...styles.formGridContainer}>
                    <GridItem {...styles.formGridItem}>
                        <TextFormField
                            {...styles.estimatedAnnualQuantityFormField}
                            defaultValue={draftFieldValues.estimatedAnnualQuantity.value}
                            label={estimatedAnnualQuantityLabel}
                            onUpdate={estimatedAnnualQuantity =>
                                dispatch(setDraftFieldValues({ estimatedAnnualQuantity }))
                            }
                            required
                            requiredErrorMessage={translate("Please enter your estimated annual quantity.")}
                            showError={showErrors}
                        />
                    </GridItem>
                    <GridItem {...styles.formGridItem}>
                        <FileUploadFormField
                            {...styles.fileUploadFormField}
                            accept=".pdf,.zip"
                            defaultValue={draftFieldValues.attachments.value}
                            label={attachmentsLabel}
                            multiple
                            onUpdate={attachments => dispatch(setDraftFieldValues({ attachments }))}
                            showError={showErrors}
                        />
                    </GridItem>
                    <GridItem {...styles.formGridItem} width={12}>
                        <TextAreaFormField
                            {...styles.requestDetailsFormField}
                            defaultValue={draftFieldValues.requestDetails.value}
                            label={requestDetailsLabel}
                            labelDescription={siteMessage("SimpleQuoteForm_RequestDetailsDescription").toString()}
                            minLength={10}
                            minLengthErrorMessage={requestDetailsErrorMessage}
                            onUpdate={requestDetails => dispatch(setDraftFieldValues({ requestDetails }))}
                            placeholder={siteMessage("SimpleQuoteForm_RequestDetailsPlaceholder").toString()}
                            required
                            requiredErrorMessage={requestDetailsErrorMessage}
                            showError={showErrors}
                        />
                    </GridItem>
                </GridContainer>
            )}
        </FormStep>
    );
};

export default SimpleQuoteFormDetailsStep;
